// pipe: 4B.8 (Task 13) — the driver offline outbox (D4: offline is load-bearing).
//
// A self-contained `idb`-backed queue: a driver completes an entire pickup with
// NO signal, the PickupCompleteBody is queued on-device (IndexedDB), then replayed
// when connectivity returns. This pulls ONLY `idb` — NOT @affine/core / nbstore /
// any DI (the lean Driver SPA bundle ships none of those).
//
// THE LOAD-BEARING SAFETY PROPERTY: a failed flush KEEPS the op (it is NEVER
// silently dropped) and surfaces the error so the UI can show it; a 2xx dequeues
// it. A double-flush does not double-create: the op is dequeued only after the
// first 2xx, so a second flush finds nothing — and re-enqueuing the SAME
// `client_op_id` does not duplicate (the store's keyPath is `client_op_id`).
// Server-side idempotency (the `client_op_id`, Tasks 6-7) is the end-to-end
// backstop that makes a double-flush safe (exactly one custody_event); the outbox
// relies on it and does not re-implement dedup beyond not double-enqueuing a key.
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

import type {
  PickupCompleteBody,
  PickupCompleteResult,
} from '../services/driver-erp';

const DB_NAME = 'pipe-driver-outbox';
const DB_VERSION = 1;
const STORE = 'ops';

/**
 * One queued pickup-completion. The three signature PNGs live inside `body` as
 * base64 strings — IndexedDB persists strings directly, so no Blob conversion is
 * needed. `attempts`/`last_error` accumulate across failed flushes so the UI can
 * surface a stuck op (the keep-on-error contract).
 */
export interface OutboxOp {
  client_op_id: string;
  order_id: string;
  body: PickupCompleteBody;
  enqueued_at: number;
  attempts: number;
  last_error?: string;
}

interface OutboxDB extends DBSchema {
  [STORE]: {
    key: string;
    value: OutboxOp;
  };
}

export type OutboxDatabase = IDBPDatabase<OutboxDB>;

/**
 * The single capability the outbox needs from the ErpService — depend on this
 * narrow interface (ISP/DIP), not the concrete `DriverErpService`, so the flush
 * is trivially testable with a stub.
 */
export interface OutboxService {
  completePickup(
    id: string,
    body: PickupCompleteBody
  ): Promise<PickupCompleteResult>;
}

/** The per-op outcome reported during a flush so the UI can surface failures. */
export type FlushOutcome = 'ok' | 'error';

export type OnFlushResult = (
  op: OutboxOp,
  outcome: FlushOutcome,
  error?: unknown
) => void;

export interface FlushSummary {
  ok: number;
  failed: number;
}

/** Open (creating/upgrading) the outbox database. The `ops` store keys on
 *  `client_op_id`, so a re-put of the same op is idempotent (no duplicate row). */
export function openOutbox(): Promise<OutboxDatabase> {
  return openDB<OutboxDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'client_op_id' });
      }
    },
  });
}

/** Persist an op. A re-`put` of the same `client_op_id` overwrites in place — it
 *  never creates a duplicate (the key is the `client_op_id`). */
export async function enqueue(db: OutboxDatabase, op: OutboxOp): Promise<void> {
  await db.put(STORE, op);
}

/** All currently-queued ops (the pending set). */
export function pendingOps(db: OutboxDatabase): Promise<OutboxOp[]> {
  return db.getAll(STORE);
}

/** Count of currently-queued ops WITHOUT materializing them — the "N pending"
 *  badge needs only a number, and `getAll` would deserialize every op including
 *  the three base64 signature PNGs just to take `.length`. */
export function pendingCount(db: OutboxDatabase): Promise<number> {
  return db.count(STORE);
}

/** Remove exactly one op by `client_op_id` (called on a 2xx). */
export async function dequeue(
  db: OutboxDatabase,
  client_op_id: string
): Promise<void> {
  await db.delete(STORE, client_op_id);
}

/**
 * Replay every pending op, serially (await each — never hammer the server). On a
 * 2xx → `dequeue` + report `'ok'`. On a thrown error → KEEP the op (do NOT drop),
 * bump `attempts`, store `last_error`, and report `'error'` with the error so the
 * UI can surface a 409-divergence / a stuck op. Returns a `{ ok, failed }` tally.
 *
 * (A permanent 4xx — e.g. a true 409 the server will never accept — stays queued +
 * flagged. Auto-discarding permanent 4xx is a future enhancement, out of scope for
 * this task: keep-on-error is the safe default — never silently lose custody data.)
 */
export async function flush(
  db: OutboxDatabase,
  svc: OutboxService,
  onResult?: OnFlushResult
): Promise<FlushSummary> {
  const ops = await pendingOps(db);
  let ok = 0;
  let failed = 0;
  for (const op of ops) {
    if ((await flushOp(db, svc, op, onResult)) === 'ok') {
      ok += 1;
    } else {
      failed += 1;
    }
  }
  return { ok, failed };
}

/**
 * Replay exactly ONE queued op by `client_op_id` — the submit path: a fresh
 * pickup's outcome must never wait behind the whole backlog replay (the
 * reconnect/manual `flush` still drains the full queue, unchanged). Same
 * try/dequeue/keep-on-error body as `flush` (the shared {@link flushOp} —
 * SSOT). An op no longer in the queue (e.g. a concurrent reconnect flush
 * already dequeued it on a 2xx) tallies `{ ok: 0, failed: 0 }` and reports
 * nothing — never a failure, and never a second server call.
 */
export async function flushOne(
  db: OutboxDatabase,
  svc: OutboxService,
  client_op_id: string,
  onResult?: OnFlushResult
): Promise<FlushSummary> {
  const op = await db.get(STORE, client_op_id);
  if (!op) {
    return { ok: 0, failed: 0 };
  }
  return (await flushOp(db, svc, op, onResult)) === 'ok'
    ? { ok: 1, failed: 0 }
    : { ok: 0, failed: 1 };
}

// The one per-op replay body shared by `flush` and `flushOne` (SSOT for the
// keep-on-error contract): a 2xx dequeues + reports 'ok'; a thrown error KEEPS
// the op (never dropped), bumps `attempts`, records `last_error`, and reports
// 'error' with the error so the UI can surface it.
async function flushOp(
  db: OutboxDatabase,
  svc: OutboxService,
  op: OutboxOp,
  onResult?: OnFlushResult
): Promise<FlushOutcome> {
  try {
    await svc.completePickup(op.order_id, op.body);
    await dequeue(db, op.client_op_id);
    onResult?.(op, 'ok');
    return 'ok';
  } catch (error) {
    // KEEP the op — bump attempts + record the error so the UI can surface it.
    const kept: OutboxOp = {
      ...op,
      attempts: op.attempts + 1,
      last_error: error instanceof Error ? error.message : String(error),
    };
    await db.put(STORE, kept);
    onResult?.(kept, 'error', error);
    return 'error';
  }
}
